import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { urls } from '../../../utils/url-utils';
import { errorHandl } from '../../../utils/network-utils';

@Injectable({
  providedIn: 'root'
})
export class StagingService {

  constructor(private http: HttpClient) {}

  /**
   * Get intake / staging trips list from MemberLink requests API
   * GET {{baseUrl}}/api/memberlink/requests?status=intake&scheduleTime=...&skipPagination=true
   */
  getStagingTrips(queryParams?: any): Observable<any> {
    let params = new HttpParams();
    params = params.append('status', (queryParams && queryParams.status) ? queryParams.status : 'intake');
    params = params.append('skipPagination', 'true');

    if (queryParams) {
      if (queryParams.date) {
        const scheduleTimeVal = Array.isArray(queryParams.date) ? queryParams.date.join(',') : queryParams.date;
        params = params.append('scheduleTime', scheduleTimeVal);
      } else if (queryParams.scheduleTime) {
        const scheduleTimeVal = Array.isArray(queryParams.scheduleTime) ? queryParams.scheduleTime.join(',') : queryParams.scheduleTime;
        params = params.append('scheduleTime', scheduleTimeVal);
      }

      if (queryParams.search) {
        params = params.append('search', queryParams.search);
      }
      if (queryParams.jobStatuses) {
        params = params.append('jobStatuses', queryParams.jobStatuses);
      }
      if (queryParams.isShowAllTrips) {
        params = params.append('isShowAllTrips', queryParams.isShowAllTrips);
      }
    }

    const url = urls.BASE_URL + urls.MEMBERLINK_REQUESTS_URL.replace(/\/$/, '');
    return this.http.get<any>(url, { params })
      .pipe(
        map((res: any) => {
          if (Array.isArray(res)) {
            return res;
          }
          if (res && res.data && Array.isArray(res.data)) {
            return res.data;
          }
          if (res && res.requests && Array.isArray(res.requests)) {
            return res.requests;
          }
          if (res && res.result && Array.isArray(res.result)) {
            return res.result;
          }
          return res || [];
        }),
        catchError(errorHandl)
      );
  }

  /**
   * Get intake count for badge counters
   * GET {{baseUrl}}/api/memberlink/requests/intakecount
   */
  getIntakeCount(): Observable<any> {
    return this.http.get<any>(urls.BASE_URL + urls.MEMBERLINK_INTAKE_COUNT_URL)
      .pipe(
        catchError(errorHandl)
      );
  }

  /**
   * Update MemberLink Request (e.g. status, rejection reason, suggested time)
   * PUT {{baseUrl}}/api/memberlink/requests/:id
   */
  updateMemberLinkRequest(id: string, payload: any): Observable<any> {
    const targetUrl = id ? (urls.BASE_URL + urls.MEMBERLINK_REQUESTS_URL + id) : (urls.BASE_URL + urls.MEMBERLINK_REQUESTS_URL);
    return this.http.put<any>(targetUrl, payload)
      .pipe(
        catchError(errorHandl)
      );
  }

  /**
   * Get a single staging trip (MemberLink request) for editing
   * GET {{baseUrl}}/api/memberlink/requests/:memberAssignId
   */
  getMemberLinkRequest(memberAssignId: string): Observable<any> {
    return this.http.get<any>(urls.BASE_URL + urls.MEMBERLINK_REQUESTS_URL + memberAssignId)
      .pipe(
        map((res: any) => (res && (res.data || res.request || res.result)) || res),
        catchError(errorHandl)
      );
  }

  /**
   * Member trip stats: eligibility window, quota usage and per-intake-status counts
   * GET {{baseUrl}}/api/memberlink/requests/trips/count?clientId=:clientId
   * Response: { total, confirmed, unconfirmed, trip_intake, used, remaining, limitReached,
   *             eligibility: { status, startDate, endDate, tripQuota } }
   */
  getMemberTripStats(clientId: string): Observable<any> {
    const params = new HttpParams().append('clientId', clientId);
    return this.http.get<any>(urls.BASE_URL + urls.MEMBERLINK_REQUESTS_URL + 'trips/count', { params })
      .pipe(
        map((res: any) => (res && res.data) || res || {}),
        catchError(errorHandl)
      );
  }

  /**
   * Create a staging trip (MemberLink request)
   * POST {{baseUrl}}/api/memberlink/requests
   */
  createMemberLinkRequest(payload: any): Observable<any> {
    return this.http.post<any>(urls.BASE_URL + urls.MEMBERLINK_REQUESTS_URL.replace(/\/$/, ''), payload)
      .pipe(
        catchError(errorHandl)
      );
  }

  /**
   * Unconfirm an intake trip with reject reason
   */
  unconfirmTrip(id: string, intakeRejectionReason: string, extraPayload: any = {}): Observable<any> {
    const payload = Object.assign({
      intakeStatus: 'unconfirmed',
      intakeRejectionReason: intakeRejectionReason
    }, extraPayload);
    return this.updateMemberLinkRequest(id, payload);
  }

  /**
   * Confirm an intake trip
   */
  confirmTrip(id: string, extraPayload: any = {}): Observable<any> {
    const payload = Object.assign({
      intakeStatus: 'confirmed',
      source: 'Panel'
    }, extraPayload);
    return this.updateMemberLinkRequest(id, payload);
  }

  /**
   * Suggest a new pickup/dropoff time for an intake trip
   */
  suggestTime(id: string, intakeSuggestedTime: string, extraPayload: any = {}): Observable<any> {
    const payload = Object.assign({
      intakeStatus: 'suggested_time',
      intakeSuggestedTime: intakeSuggestedTime
    }, extraPayload);
    return this.updateMemberLinkRequest(id, payload);
  }

  /**
   * Save a suggested plan for an intake trip with modified fields
   * PUT {{baseUrl}}/api/memberlink/requests/:id
   */
  suggestPlan(id: string, suggested: any, extraPayload: any = {}): Observable<any> {
    const payload = Object.assign({
      intakeStatus: 'suggested_plan',
      suggested: suggested
    }, extraPayload);
    return this.updateMemberLinkRequest(id, payload);
  }

  /**
   * Get MemberLink request logs history for a trip / memberAssign
   * GET {{baseUrl}}/api/memberlink/requests/logs?memberAssign=:id
   */
  getRequestLogs(memberAssignId: string): Observable<any> {
    let params = new HttpParams();
    if (memberAssignId) {
      params = params.append('memberAssign', memberAssignId);
    }
    return this.http.get<any>(urls.BASE_URL + urls.MEMBERLINK_REQUESTS_LOGS_URL, { params })
      .pipe(
        map((res: any) => {
          if (Array.isArray(res)) return res;
          if (res && res.data && Array.isArray(res.data)) return res.data;
          if (res && res.logs && Array.isArray(res.logs)) return res.logs;
          if (res && res.result && Array.isArray(res.result)) return res.result;
          return res || [];
        }),
        catchError(errorHandl)
      );
  }
}
